import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Tooltip as ChartTooltip,
} from 'chart.js';
import ZoomPlugin from 'chartjs-plugin-zoom';
import { RangePicker } from '@/shared/ui/RangePicker';
import { SearchInput } from '@/shared/ui/SearchInput';
import ReactMarkdown from 'react-markdown';
import { CausalChain } from './CausalChainGraph';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, zIndex as ZI, semantic, shadow, radius } from '@/shared/theme/tokens';
import { timeAgo, formatTs } from '@/shared/lib/formatters';
import { useRecommendationDetail, useSubmitAction, useAddRemark } from '../hooks/useRecommendations';
import { useParameterCharts } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { lastNDaysRangeFrom } from "@/shared/lib/dateRange";
import { useAuthStore } from '@/entities/session/model/authStore';
import { useApiErrorToast } from '@/shared/ui/Toast';
import {
  TOPBAR_H,
  REC_STATUS_META,
  REC_TIMELINE_META,
  CAUSAL_NODE_TYPE,
  PARAM_STATE_META,
  ACTION_OPTS,
  REC_BRAND,
} from '../recommendationMeta';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, ZoomPlugin);

// ── Helpers ───────────────────────────────────────────────────────────────────


// ── React Flow custom nodes ───────────────────────────────────────────────────


// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, icon, children, c, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${c.border}` }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
        padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: typography.fontFamily.body,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textActive }}>{icon}</span>
        <span style={{ flex: 1, textAlign: 'left', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{title}</span>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
      </button>
      {open && <div style={{ padding: '0 20px 18px' }}>{children}</div>}
    </div>
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────────

function Markdown({ content, c }) {
  return (
    <div style={{ fontSize: typography.fontSize.md, lineHeight: 1.7, color: c.textPrimary, fontFamily: typography.fontFamily.body }}>
      <ReactMarkdown
        components={{
          h2: ({ children }) => <p style={{ margin: '0 0 8px', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{children}</p>,
          p:  ({ children }) => <p style={{ margin: '0 0 8px', color: c.textMuted }}>{children}</p>,
          strong: ({ children }) => <strong style={{ color: c.textPrimary }}>{children}</strong>,
          li: ({ children }) => <li style={{ marginBottom: 4, color: c.textMuted }}>{children}</li>,
          ul: ({ children }) => <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: '0 0 8px', paddingLeft: 20 }}>{children}</ol>,
          code: ({ children }) => <code style={{ background: c.bgMuted, borderRadius: radius.xs, padding: '1px 5px', fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.mono }}>{children}</code>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// ── Model reasoning list ──────────────────────────────────────────────────────

const REASONING_TYPE_META = {
  trigger: { label: 'Trigger',  icon: 'bolt',          color: '#f59e0b', bg: 'rgba(245,158,11,0.10)'  },
  feature: { label: 'Feature',  icon: 'function',      color: semantic.infoCyan, bg: 'rgba(8,145,178,0.10)'   },
  signal:  { label: 'Signal',   icon: 'monitoring',    color: semantic.violet, bg: 'rgba(124,58,237,0.10)'  },
  rule:    { label: 'Rule',     icon: 'rule',          color: '#059669', bg: 'rgba(5,150,105,0.10)'   },
  context: { label: 'Context',  icon: 'info',          color: '#64748b', bg: 'rgba(100,116,139,0.10)' },
};

// Palette for unknown types from BE — deterministic per type string
const REASONING_FALLBACK_PALETTE = [
  { color: '#e11d48', bg: 'rgba(225,29,72,0.10)'   },
  { color: '#0284c7', bg: 'rgba(2,132,199,0.10)'   },
  { color: '#d97706', bg: 'rgba(217,119,6,0.10)'   },
  { color: semantic.violet, bg: 'rgba(124,58,237,0.10)'  },
  { color: '#059669', bg: 'rgba(5,150,105,0.10)'   },
  { color: '#db2777', bg: 'rgba(219,39,119,0.10)'  },
  { color: semantic.infoCyan, bg: 'rgba(8,145,178,0.10)'   },
];

function hashType(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % REASONING_FALLBACK_PALETTE.length;
}

function reasoningMeta(type) {
  if (REASONING_TYPE_META[type]) return REASONING_TYPE_META[type];
  const p = REASONING_FALLBACK_PALETTE[hashType(type)];
  return { label: type.charAt(0).toUpperCase() + type.slice(1), icon: 'label', color: p.color, bg: p.bg };
}

function ModelReasoningList({ items, c }) {
  if (!items?.length) return <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No model reasoning available.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {items.map((item, i) => {
        const m = reasoningMeta(item.type ?? 'context');
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Pill label */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-start',
              padding: '3px 10px', borderRadius: radius.full,
              background: m.bg, border: `1px solid ${m.color}40`,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 12, color: m.color, fontVariationSettings: "'FILL' 1" }}>{m.icon}</span>
              <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: m.color, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, whiteSpace: 'nowrap' }}>{m.label}</span>
            </div>
            {/* Content */}
            <div style={{ fontSize: typography.fontSize.md, lineHeight: 1.6, color: c.textMuted, fontFamily: typography.fontFamily.body, paddingLeft: 4 }}>
              <Markdown content={item.content} c={c} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Causal chain flow ─────────────────────────────────────────────────────────

// ── Parameter state ─────────────────────────────────────────────────────────────

function TrendChart({ data, timestamps, color, height = 80, optimalMin, optimalMax }) {
  const c = useThemeColors();
  const chartRef = React.useRef(null);
  const labels = timestamps
    ? timestamps.map((t) => {
        const d = new Date(t);
        return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      })
    : data.map((_, i) => `T-${data.length - 1 - i}`);
  const stride = Math.ceil(labels.length / 6);
  const sparseLabels = labels.map((l, i) => (i % stride === 0 ? l : ''));

  const ptColors = data.map((v) => {
    const ok = (optimalMin == null || v >= optimalMin) && (optimalMax == null || v <= optimalMax);
    return ok ? 'rgba(22,163,74,0.85)' : 'rgba(220,38,38,0.85)';
  });

  const datasets = [
    { data, borderColor: color ?? CAUSAL_NODE_TYPE.controllable.border, borderWidth: 1.5, pointRadius: 2, pointBackgroundColor: ptColors, pointBorderColor: ptColors, tension: 0.3, fill: false, label: 'Value' },
  ];
  if (optimalMin != null) datasets.push({
    data: Array(data.length).fill(optimalMin),
    borderColor: 'rgba(22,163,74,0.7)', borderWidth: 1, borderDash: [4, 3],
    pointRadius: 0, fill: false, label: 'Min', tension: 0,
  });
  if (optimalMax != null) datasets.push({
    data: Array(data.length).fill(optimalMax),
    borderColor: 'rgba(22,163,74,0.7)', borderWidth: 1, borderDash: [4, 3],
    pointRadius: 0, fill: optimalMin != null ? '-1' : false,
    backgroundColor: 'rgba(22,163,74,0.06)', label: 'Max', tension: 0,
  });

  const chartData = { labels: sparseLabels, datasets };
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'nearest', intersect: false,
        filter: (item) => item.datasetIndex === 0,
        callbacks: {
          title: (items) => {
            if (!timestamps || !items.length) return '';
            const ts = timestamps[items[0].dataIndex];
            if (!ts) return '';
            const d = new Date(ts);
            return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          },
        },
      },
      zoom: {
        pan: { enabled: false },
        zoom: {
          wheel: { enabled: false }, pinch: { enabled: false }, mode: 'xy',
          drag: { enabled: true, backgroundColor: 'rgba(30,67,150,0.07)', borderColor: 'rgba(30,67,150,0.25)', borderWidth: 1 },
        },
      },
    },
    scales: {
      x: { display: !!timestamps, ticks: { color: c.textSubtle, font: { size: 9 }, maxRotation: 0, autoSkip: false }, grid: { display: false } },
      y: { display: true, ticks: { color: c.textSubtle, font: { size: 9 }, maxTicksLimit: 4 }, grid: { color: c.border } },
    },
  };
  return (
    <div style={{ height, position: 'relative' }}>
      <Line ref={chartRef} data={chartData} options={opts} />
      <button
        onClick={() => chartRef.current?.resetZoom()}
        title="Reset zoom"
        style={{
          position: 'absolute', top: 4, right: 4,
          padding: '2px 7px', borderRadius: radius.sm, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
          border: `1px solid ${c.border}`, background: c.bg, color: c.textSubtle,
          cursor: 'pointer', fontFamily: typography.fontFamily.body,
          opacity: 0.8,
        }}
      >Reset</button>
    </div>
  );
}

const PARAM_TREND_RANGES = [
  { key: '1d',  label: '1D',  days: 1  },
  { key: '7d',  label: '7D',  days: 7  },
  { key: '14d', label: '14D', days: 14 },
  { key: '30d', label: '30D', days: 30 },
];

function ParamTrendPanel({ depId, parameterId, color, c, anchorIso }) {
  const [rangeKey, setRangeKey] = useState('7d');
  const rangeDays = PARAM_TREND_RANGES.find((r) => r.key === rangeKey)?.days ?? 7;
  const { from, to } = useMemo(() => lastNDaysRangeFrom(rangeDays, anchorIso), [rangeDays, anchorIso]);
  const { data, isLoading } = useParameterCharts(depId, parameterId, from, to, 'run_chart');

  return (
    <div style={{ paddingTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Trend</span>
        <RangePicker ranges={PARAM_TREND_RANGES} value={rangeKey} onChange={setRangeKey} color={color} c={c} size="sm" />
      </div>
      <div style={{ height: 110 }}>
        {isLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>Loading…</span>
          </div>
        ) : data?.runChart?.points?.length ? (
          <TrendChart data={data.runChart.points} timestamps={data.runChart.timestamps} color={color} height={110} optimalMin={data.runChart.optimalMin} optimalMax={data.runChart.optimalMax} />
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ margin: 0, fontSize: typography.fontSize.base, color: c.textSubtle }}>No trend data.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChips({ label, options, active, onChange, getStyle, c }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, flexShrink: 0 }}>{label}</span>
      {options.map((opt) => {
        const isActive = active === opt.key;
        const s = getStyle?.(opt.key, isActive) ?? {};
        return (
          <button
            key={opt.key}
            onClick={() => onChange(isActive ? null : opt.key)}
            style={{
              padding: '2px 9px', borderRadius: radius.full, fontSize: typography.fontSize.sm, fontWeight: isActive ? 700 : 500,
              border: `1px solid ${isActive ? (s.color ?? c.textActive) : c.border}`,
              background: isActive ? (s.bg ?? 'rgba(30,67,150,0.08)') : 'transparent',
              color: isActive ? (s.color ?? c.textActive) : c.textSubtle,
              cursor: 'pointer', fontFamily: typography.fontFamily.body,
              transition: 'all 0.12s',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ParameterStatePanel({ parameterState, depId, recId: _recId, c, isDark, anchorIso }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [layerFilter, setLayerFilter] = useState(null);
  const [stateFilter, setStateFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const items = parameterState ?? [];

  // Derive unique layers from data
  const layers = [...new Set(items.map((p) => p.layer).filter(Boolean))].sort();

  const STATE_OPTS = [
    { key: 'in_range',    label: 'In Range'     },
    { key: 'out_of_range',label: 'Out of Range' },
    { key: 'off',         label: 'Off'          },
  ];

  const TYPE_OPTS = [
    { key: 'target',       label: 'Target'       },
    { key: 'controllable', label: 'Controllable' },
    { key: 'observed',     label: 'Observed'     },
    { key: 'monitored',    label: 'Monitored'    },
  ];

  const filtered = items.filter((p) => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.key.toLowerCase().includes(search.toLowerCase())) return false;
    if (layerFilter && p.layer !== layerFilter) return false;
    if (stateFilter && p.state !== stateFilter) return false;
    if (typeFilter && p.paramType !== typeFilter) return false;
    return true;
  });

  const activeColor = CAUSAL_NODE_TYPE.controllable.border;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: 460, minHeight: 0 }}>
      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search parameters…"
        size="sm"
        containerStyle={{ flexShrink: 0 }}
      />

      {/* Filter rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flexShrink: 0 }}>
        {layers.length > 0 && (
          <FilterChips
            label="Layer"
            options={layers.map((l) => ({ key: l, label: l }))}
            active={layerFilter}
            onChange={setLayerFilter}
            c={c}
          />
        )}
        <FilterChips
          label="State"
          options={STATE_OPTS}
          active={stateFilter}
          onChange={setStateFilter}
          getStyle={(key) => {
            const m = PARAM_STATE_META[key];
            return m ? { color: m.color, bg: m.bg } : {};
          }}
          c={c}
        />
        <FilterChips
          label="Type"
          options={TYPE_OPTS}
          active={typeFilter}
          onChange={setTypeFilter}
          getStyle={(key) => {
            const m = CAUSAL_NODE_TYPE[key];
            return m ? { color: m.text, bg: m.bg } : {};
          }}
          c={c}
        />
      </div>

      {/* Scrollable list */}
      <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: 2 }}>
        {filtered.map((p) => {
          const pill = PARAM_STATE_META[p.state] ?? PARAM_STATE_META.off;
          const typeMeta = CAUSAL_NODE_TYPE[p.paramType];
          const isOpen = expanded === p.id;
          return (
            <div key={p.id} style={{
              border: `1px solid ${isOpen ? activeColor : c.border}`, borderRadius: radius.lg,
              background: isOpen ? (isDark ? `${activeColor}0f` : `${activeColor}08`) : c.bg,
            }}>
              <div
                onClick={() => setExpanded(isOpen ? null : p.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', cursor: 'pointer' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: 2 }}>
                    <p style={{ margin: 0, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{p.name}</p>
                    {typeMeta && (
                      <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: typeMeta.text, background: typeMeta.bg, borderRadius: radius.xs, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, flexShrink: 0 }}>
                        {typeMeta.label}
                      </span>
                    )}
                  </div>
                  {p.layer && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle, background: c.bgMuted, borderRadius: radius.xs, padding: '0px 4px' }}>{p.layer}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary, whiteSpace: 'nowrap' }}>
                  {p.value} <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, color: c.textSubtle }}>{p.unit}</span>
                </span>
                <span style={{
                  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: pill.color,
                  background: pill.bg, borderRadius: radius.sm, padding: '2px 8px', whiteSpace: 'nowrap',
                }}>{pill.label}</span>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle }}>
                  {isOpen ? 'expand_less' : 'show_chart'}
                </span>
              </div>
              {isOpen && (
                <div style={{ padding: '0 14px 14px' }}>
                  <ParamTrendPanel depId={depId} parameterId={p.parameterId} color={pill.color} c={c} anchorIso={anchorIso} />
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle, textAlign: 'center', padding: '20px 0' }}>No parameters match.</p>
        )}
      </div>
    </div>
  );
}

// ── State section ─────────────────────────────────────────────────────────────

function StateSection({ rec, depId, detail, c, isDark: _isDark, isHistorical }) {
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState('');
  const toastApiError = useApiErrorToast();
  const submit = useSubmitAction(depId, rec.id);
  const currentUser = useAuthStore((s) => s.user);

  // Derive current status from timeline so the card updates immediately after
  // submit without waiting for the parent live-list query to refetch.
  const lastAction = (detail?.timeline ?? []).find(
    (t) => ['accepted', 'acted_on', 'rejected', 'expired', 'unacknowledged'].includes(t.type)
  );
  const derivedStatus = lastAction?.type ?? rec.status;

  const isExpired = derivedStatus === 'expired';
  const hasCurrentStatus = derivedStatus !== 'unacknowledged';
  const isMine = !!currentUser?.name && lastAction?.actor === currentUser.name;

  // Which action buttons to show based on current status:
  // unacknowledged → all 3
  // accepted       → only acted_on + rejected (next logical steps)
  // acted_on / rejected → all 3 (allow override)
  // expired        → none
  const visibleOpts = (() => {
    if (isExpired || isHistorical) return [];
    // Hide "Accept" only when YOU accepted it — Undo is the right action then.
    // If someone else accepted, show all 3 so you can transfer ownership.
    if (derivedStatus === 'accepted' && isMine) return ACTION_OPTS.filter((o) => o.key !== 'accepted');
    return ACTION_OPTS;
  })();

  async function handleSubmit() {
    if (!comment.trim() || submit.isPending) return;
    try {
      await submit.mutateAsync({ action: active, comment: comment.trim() });
      setActive(null);
      setComment('');
    } catch (err) { toastApiError(err); }
  }

  async function handleUndo() {
    try {
      await submit.mutateAsync({ action: 'unacknowledged', comment: 'Reverted.' });
    } catch (err) { toastApiError(err); }
  }

  const activeOpt = ACTION_OPTS.find((o) => o.key === active);
  const hasComment = comment.trim().length > 0;

  function statusIcon(s) {
    if (s === 'acted_on') return 'task_alt';
    if (s === 'rejected') return 'cancel';
    if (s === 'expired') return 'schedule';
    if (s === 'accepted') return 'check_circle';
    return 'radio_button_unchecked';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* Context card — shown whenever a status has been set */}
      {hasCurrentStatus && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '11px 14px',
          background: (REC_STATUS_META[derivedStatus] ?? REC_STATUS_META.expired).bg,
          border: `1px solid ${(REC_STATUS_META[derivedStatus] ?? REC_STATUS_META.expired).border}`,
          borderRadius: radius.lg,
        }}>
          <span className="material-symbols-outlined" style={{
            fontSize: 17, marginTop: 1,
            color: (REC_STATUS_META[derivedStatus] ?? REC_STATUS_META.expired).color,
            fontVariationSettings: "'FILL' 1",
          }}>
            {statusIcon(derivedStatus)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: (REC_STATUS_META[derivedStatus] ?? REC_STATUS_META.expired).color }}>
                {(REC_STATUS_META[derivedStatus] ?? REC_STATUS_META.expired).label}
              </span>
              {lastAction?.actor && (
                <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>by {lastAction.actor}</span>
              )}
              {lastAction?.timestamp && (
                <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, opacity: 0.75 }}>· {timeAgo(lastAction.timestamp)}</span>
              )}
              {lastAction?.timestamp && (
                <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, opacity: 0.6 }}>{formatTs(lastAction.timestamp)}</span>
              )}
            </div>
            {lastAction?.comment && (
              <p style={{ margin: '3px 0 0', fontSize: typography.fontSize.base, color: c.textMuted, lineHeight: 1.5 }}>
                {lastAction.comment}
              </p>
            )}
          </div>
          {/* Undo — only shown to the person who set the current status, not for expired */}
          {isMine && !isExpired && !isHistorical && (
            <button onClick={handleUndo} disabled={submit.isPending} style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', borderRadius: radius.md,
              border: `1px solid ${c.border}`, background: 'none',
              color: c.textSubtle, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily.body,
              cursor: submit.isPending ? 'not-allowed' : 'pointer',
              opacity: submit.isPending ? 0.5 : 1,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>undo</span>
              Undo
            </button>
          )}
        </div>
      )}

      {/* Action buttons */}
      {visibleOpts.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '8px' }}>
            {visibleOpts.map((opt) => {
              const isSelected = active === opt.key;
              const highlight = isSelected;
              return (
                <button key={opt.key} onClick={() => setActive(active === opt.key ? null : opt.key)} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                  padding: '8px 10px', borderRadius: radius.lg, cursor: 'pointer',
                  border: `1.5px solid ${highlight ? opt.color : c.border}`,
                  background: highlight ? opt.bg : 'transparent',
                  color: highlight ? opt.color : c.textSubtle,
                  fontSize: typography.fontSize.base, fontWeight: highlight ? 700 : 500,
                  fontFamily: typography.fontFamily.body,
                  transition: 'all 0.15s',
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{opt.icon}</span>
                  {opt.label}
                </button>
              );
            })}
          </div>

          {active && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <textarea
                value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder={`Add a mandatory remark for "${activeOpt?.label}"…`}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: radius.lg,
                  border: `1px solid ${hasComment ? c.border : REC_STATUS_META.rejected.color}`,
                  background: c.bgSubtle, color: c.textPrimary,
                  fontSize: typography.fontSize.md, fontFamily: typography.fontFamily.body,
                  resize: 'vertical', outline: 'none', lineHeight: 1.5,
                }}
              />
              {!hasComment && (
                <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: REC_STATUS_META.rejected.color }}>Remark is required before submitting.</p>
              )}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => { setActive(null); setComment(''); }} style={{
                  padding: '7px 16px', borderRadius: radius.md, border: `1px solid ${c.border}`,
                  background: 'none', color: c.textSubtle, cursor: 'pointer',
                  fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.body,
                }}>Cancel</button>
                <button
                  onClick={handleSubmit}
                  disabled={!hasComment || submit.isPending}
                  style={{
                    padding: '7px 16px', borderRadius: radius.md, border: 'none', cursor: hasComment ? 'pointer' : 'not-allowed',
                    background: activeOpt?.color ?? REC_BRAND,
                    color: '#fff', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold,
                    fontFamily: typography.fontFamily.body,
                    opacity: (!hasComment || submit.isPending) ? 0.6 : 1,
                  }}
                >
                  {submit.isPending ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Chat timeline ─────────────────────────────────────────────────────────────

function Timeline({ entries, depId, recId, c }) {
  const [remark, setRemark] = useState('');
  const toastApiError = useApiErrorToast();
  const addRemark = useAddRemark(depId, recId);

  async function handleAdd() {
    if (!remark.trim()) return;
    try {
      await addRemark.mutateAsync(remark.trim());
      setRemark('');
    } catch (err) { toastApiError(err); }
  }

  const sorted = [...(entries ?? [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {sorted.map((entry, i) => {
        const meta = REC_TIMELINE_META[entry.type] ?? REC_TIMELINE_META.remark;
        const isLast = i === sorted.length - 1;
        return (
          <div key={entry.id} style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: `${meta.color}15`, border: `1.5px solid ${meta.color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: meta.color }}>{meta.icon}</span>
              </div>
              {!isLast && <div style={{ width: 1, flex: 1, background: c.border, marginTop: 4 }} />}
            </div>
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '3px' }}>
                <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{entry.actor}</span>
                <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{formatTs(entry.timestamp)}</span>
              </div>
              <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textMuted, lineHeight: 1.5 }}>{entry.comment}</p>
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: 4 }}>
        <textarea
          value={remark} onChange={(e) => setRemark(e.target.value)}
          placeholder="Add a remark…"
          rows={2}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: radius.lg,
            border: `1px solid ${c.border}`, background: c.bgSubtle,
            color: c.textPrimary, fontSize: typography.fontSize.md,
            fontFamily: typography.fontFamily.body,
            resize: 'none', outline: 'none', lineHeight: 1.5,
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(); } }}
        />
        <button onClick={handleAdd} disabled={!remark.trim() || addRemark.isPending} style={{
          padding: '8px 14px', borderRadius: radius.lg, border: 'none',
          background: REC_BRAND, color: '#fff', cursor: remark.trim() ? 'pointer' : 'not-allowed',
          fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, fontFamily: typography.fontFamily.body,
          opacity: (!remark.trim() || addRemark.isPending) ? 0.5 : 1,
          flexShrink: 0,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>send</span>
        </button>
      </div>
    </div>
  );
}

// ── Main drawer ───────────────────────────────────────────────────────────────

export function RecommendationDrawer({ rec, depId, isHistorical, onClose, isOpen = true, recs = [], recIndex = 0, onNavigate }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [mounted, setMounted] = useState(false);
  const { data: detail, isLoading } = useRecommendationDetail(depId, rec?.id);

  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e) {
      if (e.key === 'ArrowLeft') onNavigate?.((recIndex - 1 + recs.length) % recs.length);
      if (e.key === 'ArrowRight') onNavigate?.((recIndex + 1) % recs.length);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, recIndex, recs.length, onNavigate]);

  // One-tick delay so CSS transition fires on enter (browser must paint closed state first)
  useEffect(() => { const id = setTimeout(() => setMounted(true), 10); return () => clearTimeout(id); }, []);

  if (!rec) return null;

  const open = isOpen && mounted;

  const sm = REC_STATUS_META[rec.status] ?? REC_STATUS_META.unacknowledged;

  function actionSummary(action, unit) {
    if (!action) return '—';
    if (action.type === 'numerical' || action.type === 'number') return `${action.currentValue} → ${action.recommendedValue} ${unit ?? ''}`.trim();
    return action.instruction;
  }

  return (
    <>
      {/* Backdrop — starts below topbar */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: TOPBAR_H, left: 0, right: 0, bottom: 0,
          background: semantic.overlaySoft, zIndex: ZI.topbar + 10, backdropFilter: 'blur(1px)',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.25s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: TOPBAR_H, right: 0, bottom: 0,
        width: 600, zIndex: ZI.topbar + 11,
        background: c.bg, borderLeft: `1px solid ${c.border}`,
        display: 'flex', flexDirection: 'column',
        boxShadow: shadow.drawer,
        fontFamily: typography.fontFamily.body,
        overflowY: 'auto',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px 14px', borderBottom: `1px solid ${c.border}`,
          position: 'sticky', top: 0, background: c.bg, zIndex: 1,
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 4px', fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, lineHeight: 1.3 }}>
                {rec.parameterName}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{timeAgo(rec.generatedAt)}</span>
                <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{formatTs(rec.generatedAt)}</span>
                <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>·</span>
                <span style={{
                  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: sm.color,
                  background: sm.bg, border: `1px solid ${sm.border}`,
                  borderRadius: radius.sm, padding: '2px 8px',
                  textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label,
                }}>
                  {sm.label}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              {recs.length > 1 && (
                <>
                  <button onClick={() => onNavigate?.((recIndex - 1 + recs.length) % recs.length)} title="Previous (←)" style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: c.textSubtle, borderRadius: radius.sm,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>chevron_left</span>
                  </button>
                  <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    {recIndex + 1}/{recs.length}
                  </span>
                  <button onClick={() => onNavigate?.((recIndex + 1) % recs.length)} title="Next (→)" style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: c.textSubtle, borderRadius: radius.sm,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>chevron_right</span>
                  </button>
                  <div style={{ width: 1, height: 16, background: c.border, margin: '0 4px' }} />
                </>
              )}
              <button onClick={onClose} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                color: c.textSubtle, borderRadius: radius.sm,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>
          </div>

          {/* Action summary */}
          <div style={{
            background: isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle,
            borderRadius: radius.lg, padding: '10px 14px',
            fontSize: typography.fontSize.md, color: c.textMuted, lineHeight: 1.5,
          }}>
            <span style={{ fontWeight: typography.fontWeight.semibold, color: c.textActive, display: 'block', marginBottom: rec.action?.instruction ? 4 : 0 }}>Action</span>
            {rec.action?.instruction
              ? <Markdown content={rec.action.instruction} c={c} />
              : actionSummary(rec.action, rec.unit)}
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div style={{ padding: 24, color: c.textSubtle, fontSize: typography.fontSize.md }}>Loading detail…</div>
        ) : (
          <>
            <Section title="Status & Response" icon="manage_accounts" c={c} defaultOpen>
              <StateSection rec={rec} depId={depId} detail={detail} c={c} isDark={isDark} isHistorical={isHistorical} />
            </Section>

            {isHistorical && (
              <Section title="Expected Impact" icon="trending_up" c={c} defaultOpen>
                {(() => {
                  const impacts = detail?.actualImpact ?? rec.impacts;
                  if (!impacts?.length) return <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No impact data.</p>;
                  const headerCell = { fontSize: '10px', fontWeight: 700, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: 6 };
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr max-content max-content max-content', columnGap: 20 }}>
                      {/* Header */}
                      <span style={headerCell}>Variable</span>
                      <span style={{ ...headerCell, textAlign: 'right' }}>Expected</span>
                      <span style={{ ...headerCell, textAlign: 'right' }}>Actual</span>
                      <span style={{ ...headerCell, textAlign: 'right' }}>Delay</span>
                      {/* Divider spanning all 4 columns */}
                      <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${c.border}`, margin: 0 }} />
                      {impacts.map((imp, i) => (
                        <React.Fragment key={i}>
                          <span style={{ fontSize: typography.fontSize.md, color: c.textPrimary, fontWeight: typography.fontWeight.medium, padding: '8px 0', borderBottom: i < impacts.length - 1 ? `1px solid ${c.border}` : 'none' }}>{imp.variable}</span>
                          <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle, textAlign: 'right', whiteSpace: 'nowrap', padding: '8px 0', borderBottom: i < impacts.length - 1 ? `1px solid ${c.border}` : 'none' }}>{imp.expected ?? '—'}</span>
                          <span style={{ fontSize: typography.fontSize.md, fontWeight: imp.actual ? 700 : 400, color: imp.actual ? REC_STATUS_META.acted_on.color : c.textSubtle, textAlign: 'right', whiteSpace: 'nowrap', padding: '8px 0', borderBottom: i < impacts.length - 1 ? `1px solid ${c.border}` : 'none' }}>{imp.actual ?? '—'}</span>
                          <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle, textAlign: 'right', whiteSpace: 'nowrap', padding: '8px 0', borderBottom: i < impacts.length - 1 ? `1px solid ${c.border}` : 'none' }}>{imp.delay ?? '—'}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </Section>
            )}

            <Section title="Why — Rationale" icon="psychology" c={c} defaultOpen>
              {detail?.rationale ? <Markdown content={detail.rationale} c={c} /> : <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No rationale available.</p>}
              {detail?.causalChain && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ margin: '0 0 8px', fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Causal Chain</p>
                  <CausalChain chain={detail.causalChain} c={c} isDark={isDark} />
                </div>
              )}
            </Section>

            <Section title="Evidence" icon="article" c={c} defaultOpen>
              {detail?.evidence ? <Markdown content={detail.evidence} c={c} /> : <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>No evidence available.</p>}
            </Section>

            <Section title="Parameter State at Recommendation" icon="sensors" c={c} defaultOpen={false}>
              <ParameterStatePanel parameterState={detail?.furnaceState} depId={depId} recId={rec.id} c={c} isDark={isDark} anchorIso={rec.generatedAt} />
            </Section>

            <Section title="Model Reasoning" icon="model_training" c={c} defaultOpen={false}>
              <ModelReasoningList items={detail?.modelReasoning} c={c} />
            </Section>

            <Section title="Remarks & Timeline" icon="forum" c={c} defaultOpen={false}>
              <Timeline entries={detail?.timeline} depId={depId} recId={rec.id} c={c} isDark={isDark} />
            </Section>
          </>
        )}
      </div>
    </>
  );
}
